import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import {
  ArrowLeft, User, Mail, Phone, GraduationCap, Award, FileText,
  CheckCircle, XCircle, Clock, MessageSquare, DollarSign, Send,
  Calendar, MapPin, Briefcase, Loader2
} from 'lucide-react';

const ApplicationReviewUniversity = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState('');
  const [message, setMessage] = useState('');
  const [scholarshipAmount, setScholarshipAmount] = useState('');
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const universityId = localStorage.getItem('university_id');
  const universityName = localStorage.getItem('university_name') || 'University';

  useEffect(() => {
    if (!universityId) {
      navigate('/university/login');
      return;
    }
    fetchApplicationDetails();
  }, [applicationId, universityId, navigate]);

  const fetchApplicationDetails = async () => {
    try {
      const response = await api.get(`/university-hub/applications/${applicationId}?university_id=${universityId}`);
      setApplication(response.data.application);
      setStudent(response.data.student);
      setMessages(response.data.application?.messages || []);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load application", variant: "destructive" });
      navigate('/university/applications');
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async () => {
    if (!decision) {
      toast({ title: "Error", description: "Please select a decision", variant: "destructive" });
      return;
    }

    setDecisionLoading(true);
    try {
      await api.post(`/university-hub/applications/${applicationId}/decision?university_id=${universityId}`, {
        application_id: applicationId,
        decision: decision,
        message: message,
        scholarship_offered: scholarshipAmount ? parseFloat(scholarshipAmount) : null
      });
      toast({ title: "Success", description: "Decision recorded successfully" });
      fetchApplicationDetails();
    } catch (error) {
      toast({ title: "Error", description: "Failed to record decision", variant: "destructive" });
    } finally {
      setDecisionLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    setSendingMessage(true);
    try {
      await api.post(`/university-hub/messages/send?university_id=${universityId}`, {
        application_id: applicationId,
        message: message,
        subject: `Message regarding your application`
      });
      toast({ title: "Message Sent", description: "The applicant has been notified via email" });
      setMessage('');
      // Refresh to get new messages
      fetchApplicationDetails();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send message", variant: "destructive" });
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getDecisionBadge = () => {
    const d = application?.decision;
    if (d === 'accepted') return <Badge className="bg-green-100 text-green-700 text-lg px-4 py-2">Accepted</Badge>;
    if (d === 'denied') return <Badge className="bg-red-100 text-red-700 text-lg px-4 py-2">Denied</Badge>;
    if (d === 'waitlisted') return <Badge className="bg-purple-100 text-purple-700 text-lg px-4 py-2">Waitlisted</Badge>;
    if (d === 'deferred') return <Badge className="bg-orange-100 text-orange-700 text-lg px-4 py-2">Deferred</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-700 text-lg px-4 py-2">Pending Decision</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading application...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/university/applications">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Applications
                </Button>
              </Link>
              <h1 className="text-xl font-semibold">Application Review</h1>
            </div>
            {getDecisionBadge()}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Student Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Student Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-600">Full Name</Label>
                    <p className="font-medium">{student?.first_name} {student?.last_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Email</Label>
                    <p className="font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {student?.email}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Date of Birth</Label>
                    <p className="font-medium">{student?.date_of_birth || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Citizenship</Label>
                    <p className="font-medium">{student?.citizenship || 'Not provided'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Academic Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-600">High School</Label>
                    <p className="font-medium">{application?.education?.school_name || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">GPA</Label>
                    <p className="font-medium">{application?.education?.gpa || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">SAT Score</Label>
                    <p className="font-medium">{application?.test_scores?.sat || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">ACT Score</Label>
                    <p className="font-medium">{application?.test_scores?.act || 'Not provided'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {application?.essays?.personal_statement && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Personal Statement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none">
                    <p className="whitespace-pre-wrap text-gray-700">
                      {application.essays.personal_statement}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Decision Panel */}
          <div className="space-y-6">
            {!application?.decision ? (
              <Card>
                <CardHeader>
                  <CardTitle>Make Decision</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Decision</Label>
                    <Select value={decision} onValueChange={setDecision}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select decision" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accepted">
                          <span className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            Accept
                          </span>
                        </SelectItem>
                        <SelectItem value="denied">
                          <span className="flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-red-600" />
                            Deny
                          </span>
                        </SelectItem>
                        <SelectItem value="waitlisted">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-purple-600" />
                            Waitlist
                          </span>
                        </SelectItem>
                        <SelectItem value="deferred">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-orange-600" />
                            Defer
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {decision === 'accepted' && (
                    <div>
                      <Label>Scholarship Offered ($)</Label>
                      <Input
                        type="number"
                        value={scholarshipAmount}
                        onChange={(e) => setScholarshipAmount(e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Message to Student (Optional)</Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Add a personal message..."
                      rows={4}
                    />
                  </div>

                  <Button
                    className="w-full"
                    onClick={handleDecision}
                    disabled={!decision || decisionLoading}
                  >
                    {decisionLoading ? "Submitting..." : "Submit Decision"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Decision Recorded</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    {getDecisionBadge()}
                    {application.scholarship_offered && (
                      <p className="mt-4 text-gray-600">
                        <DollarSign className="w-4 h-4 inline" />
                        Scholarship: ${application.scholarship_offered.toLocaleString()}
                      </p>
                    )}
                    {application.decision_date && (
                      <p className="mt-2 text-sm text-gray-500">
                        Decision made on {new Date(application.decision_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Messages with Applicant
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Message Thread */}
                {messages.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto space-y-3 mb-4 border rounded-lg p-3 bg-gray-50">
                    {messages.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg ${
                          msg.from === 'university' 
                            ? 'bg-emerald-50 ml-4 border-l-4 border-emerald-400' 
                            : 'bg-white mr-4 border-l-4 border-blue-400'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">
                            {msg.from === 'university' ? universityName : (msg.from_name || 'Student')}
                          </span>
                          <span className="text-xs text-gray-500">{formatMessageTime(msg.timestamp)}</span>
                        </div>
                        <p className="text-sm text-gray-700">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                    No messages yet. Start a conversation with the applicant.
                  </p>
                )}

                {/* Compose Message */}
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message to the applicant..."
                  rows={3}
                  data-testid="message-textarea"
                />
                <Button 
                  className="w-full" 
                  onClick={sendMessage} 
                  disabled={!message.trim() || sendingMessage}
                  data-testid="send-message-btn"
                >
                  {sendingMessage ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Message
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationReviewUniversity;
